import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getLeads, createLead, updateLeadStatus, addLeadContact, deleteLead } from '../lib/api';
import { formatDate, formatDateTime, statusLabels, getStatusClass } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  UserPlus, 
  Phone,
  Mail,
  MessageSquare,
  Loader2,
  History,
  ExternalLink,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const leadStatuses = [
  { value: 'nuevo', label: 'Nuevo' },
  { value: 'pendiente_contacto', label: 'Pendiente de Contacto' },
  { value: 'contactado', label: 'Contactado' },
  { value: 'agendo_clase_prueba', label: 'Agendó Clase de Prueba' },
  { value: 'no_responde', label: 'No Responde' },
  { value: 'interesado', label: 'Interesado' },
  { value: 'convertido', label: 'Convertido' },
  { value: 'descartado', label: 'Descartado' },
];

const contactChannels = [
  { value: 'telefono', label: 'Teléfono' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'presencial', label: 'Presencial' },
];

const contactResults = [
  { value: 'contestó', label: 'Contestó' },
  { value: 'no_contestó', label: 'No Contestó' },
  { value: 'mensaje_dejado', label: 'Mensaje Dejado' },
  { value: 'agendó_cita', label: 'Agendó Cita' },
  { value: 'no_interesado', label: 'No Interesado' },
];

const Leads = () => {
  const { isDirection } = useAuth();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showNewDialog, setShowNewDialog] = useState(searchParams.get('action') === 'new');
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    source: 'manual',
    campaign: ''
  });
  const [contactData, setContactData] = useState({
    channel: '',
    result: '',
    observations: '',
    next_followup: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeads = async () => {
    try {
      const response = await getLeads({ status: statusFilter || undefined });
      setLeads(response.data);
    } catch (error) {
      
      toast.error('Error al cargar leads');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await createLead(formData);
      toast.success('Lead creado');
      setShowNewDialog(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        source: 'manual',
        campaign: ''
      });
      fetchLeads();
    } catch (error) {
      
      toast.error('Error al crear lead');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await updateLeadStatus(leadId, newStatus);
      toast.success('Estado actualizado');
      fetchLeads();
    } catch (error) {
      
      toast.error('Error al actualizar estado');
    }
  };

  const handleDeleteLead = async (lead) => {
    if (!window.confirm(`Eliminar lead "${lead.name}"? Esta accion no se puede deshacer.`)) return;
    try {
      await deleteLead(lead.id);
      toast.success('Lead eliminado');
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleViewLead = (lead) => {
    setSelectedLead(lead);
    setShowDetailDialog(true);
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await addLeadContact(selectedLead.id, contactData);
      toast.success('Contacto registrado');
      setShowContactDialog(false);
      setContactData({
        channel: '',
        result: '',
        observations: '',
        next_followup: ''
      });
      fetchLeads();
      // Refresh selected lead
      const updated = await getLeads();
      const refreshedLead = updated.data.find(l => l.id === selectedLead.id);
      if (refreshedLead) setSelectedLead(refreshedLead);
    } catch (error) {
      
      toast.error('Error al registrar contacto');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      nuevo: 'bg-[#FAF0EC] text-[#D98A6C] border-[#D98A6C]/20',
      pendiente_contacto: 'bg-[#F5F0E8] text-[#5C7083] border-[#E8E2D7]',
      contactado: 'bg-[#EDF5F0] text-[#4A7C59] border-[#4A7C59]/20',
      agendo_clase_prueba: 'bg-[#EDF5F0] text-[#4A7C59] border-[#4A7C59]/20',
      no_responde: 'bg-[#F5F0E8] text-[#5C7083] border-[#E8E2D7]',
      interesado: 'bg-[#EDF5F0] text-[#4A7C59] border-[#4A7C59]/20',
      convertido: 'bg-[#2B4257] text-white border-[#2B4257]',
      descartado: 'bg-[#FBEAE9] text-[#C25953] border-[#C25953]/20',
    };
    return classes[status] || 'bg-[#F5F0E8] text-[#5C7083]';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="leads-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" />
      </div>
    );
  }

  const newLeads = leads.filter(l => l.status === 'nuevo' || l.status === 'pendiente_contacto').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="page-subtitle">
            Seguimiento de prospectos y clases de prueba
            {newLeads > 0 && <span className="ml-2 text-[#D98A6C]">({newLeads} nuevos)</span>}
          </p>
        </div>
        <Button 
          className="btn-primary gap-2" 
          onClick={() => setShowNewDialog(true)}
          data-testid="btn-new-lead"
        >
          <Plus size={18} />
          Nuevo Lead
        </Button>
      </div>

      {/* Filter */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? "" : value)}>
            <SelectTrigger className="w-full md:w-[200px]" data-testid="select-lead-status-filter">
              <SelectValue placeholder="Todos los estados" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {leadStatuses.map((status) => (
                <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Leads Table */}
      <div className="card p-0 overflow-hidden" data-testid="leads-table">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Contacto</th>
                <th>Origen</th>
                <th>Estado</th>
                <th>Fecha</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12">
                    <UserPlus className="mx-auto h-12 w-12 text-[#E8E2D7] mb-4" />
                    <p className="text-[#5C7083]">No hay leads registrados</p>
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
                    <td>
                      <p className="font-medium text-[#1A2835]">{lead.name}</p>
                      {lead.campaign && <p className="text-xs text-[#5C7083]">{lead.campaign}</p>}
                    </td>
                    <td>
                      <div className="space-y-1">
                        {lead.email && (
                          <p className="text-sm flex items-center gap-1">
                            <Mail size={12} className="text-[#5C7083]" />
                            {lead.email}
                          </p>
                        )}
                        {lead.phone && (
                          <p className="text-sm flex items-center gap-1">
                            <Phone size={12} className="text-[#5C7083]" />
                            {lead.phone}
                          </p>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="status-badge badge-default">
                        {lead.source === 'shopify' ? 'Shopify' : 
                         lead.source === 'manual' ? 'Manual' : lead.source || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <Select 
                        value={lead.status} 
                        onValueChange={(value) => handleStatusChange(lead.id, value)}
                      >
                        <SelectTrigger 
                          className={`w-[180px] border ${getStatusBadgeClass(lead.status)}`}
                          data-testid={`select-status-${lead.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {leadStatuses.map((status) => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td>{formatDate(lead.created_at)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="btn-ghost p-2"
                          onClick={() => handleViewLead(lead)}
                          data-testid={`btn-view-lead-${lead.id}`}
                        >
                          <History size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="btn-ghost p-2"
                          onClick={() => {
                            setSelectedLead(lead);
                            setShowContactDialog(true);
                          }}
                          data-testid={`btn-contact-lead-${lead.id}`}
                        >
                          <MessageSquare size={16} />
                        </Button>
                        {isDirection() && (
                          <Button variant="ghost" size="sm"
                            className="btn-ghost p-2 text-[#C25953] hover:text-[#A63D3D] hover:bg-[#FBEAE9]"
                            onClick={() => handleDeleteLead(lead)}
                            data-testid={`btn-delete-lead-${lead.id}`}>
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Lead Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo Lead</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLead} className="space-y-4" data-testid="lead-form">
            <div>
              <Label className="form-label">Nombre *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input"
                required
                data-testid="input-lead-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input"
                  data-testid="input-lead-email"
                />
              </div>
              <div>
                <Label className="form-label">Teléfono</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="form-input"
                  data-testid="input-lead-phone"
                />
              </div>
            </div>

            <div>
              <Label className="form-label">Campaña / Origen</Label>
              <Input
                value={formData.campaign}
                onChange={(e) => setFormData({ ...formData, campaign: e.target.value })}
                className="form-input"
                placeholder="Ej: PROMOS, Instagram, Referido..."
                data-testid="input-lead-campaign"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowNewDialog(false)}
                className="btn-secondary"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="btn-primary" 
                disabled={saving || !formData.name}
                data-testid="btn-save-lead"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Crear Lead
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalle del Lead</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-6" data-testid="lead-detail">
              {/* Lead Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#5C7083]">Nombre</p>
                  <p className="font-medium">{selectedLead.name}</p>
                </div>
                <div>
                  <p className="text-sm text-[#5C7083]">Estado</p>
                  <span className={`status-badge ${getStatusBadgeClass(selectedLead.status)}`}>
                    {statusLabels[selectedLead.status]}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-[#5C7083]">Email</p>
                  <p>{selectedLead.email || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-[#5C7083]">Teléfono</p>
                  <p>{selectedLead.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-[#5C7083]">Origen</p>
                  <p>{selectedLead.source || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-[#5C7083]">Campaña</p>
                  <p>{selectedLead.campaign || '-'}</p>
                </div>
              </div>

              {/* Contact History */}
              <div>
                <h4 className="font-semibold text-[#1A2835] mb-3">Historial de Contacto</h4>
                <div className="max-h-[250px] overflow-y-auto space-y-2">
                  {selectedLead.contact_history?.length === 0 ? (
                    <p className="text-[#5C7083] text-sm py-4 text-center">No hay contactos registrados</p>
                  ) : (
                    selectedLead.contact_history?.map((contact) => (
                      <div 
                        key={contact.id}
                        className="p-3 bg-[#F5F0E8] rounded-lg"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="status-badge badge-default">{contact.channel}</span>
                          <span className="text-xs text-[#5C7083]">{formatDateTime(contact.created_at)}</span>
                        </div>
                        <p className="text-sm font-medium">{contact.result}</p>
                        {contact.observations && (
                          <p className="text-sm text-[#5C7083] mt-1">{contact.observations}</p>
                        )}
                        <p className="text-xs text-[#5C7083] mt-1">Por: {contact.user_name}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDetailDialog(false)}
                  className="btn-secondary"
                >
                  Cerrar
                </Button>
                <Button 
                  className="btn-primary"
                  onClick={() => {
                    setShowDetailDialog(false);
                    setShowContactDialog(true);
                  }}
                >
                  Registrar Contacto
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar Contacto</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <form onSubmit={handleAddContact} className="space-y-4" data-testid="contact-form">
              <div className="p-3 bg-[#F5F0E8] rounded-lg">
                <p className="text-sm text-[#5C7083]">Lead</p>
                <p className="font-medium">{selectedLead.name}</p>
              </div>

              <div>
                <Label className="form-label">Canal *</Label>
                <Select 
                  value={contactData.channel || undefined} 
                  onValueChange={(value) => setContactData({ ...contactData, channel: value })}
                >
                  <SelectTrigger data-testid="select-contact-channel">
                    <SelectValue placeholder="Seleccionar canal" />
                  </SelectTrigger>
                  <SelectContent>
                    {contactChannels.map((channel) => (
                      <SelectItem key={channel.value} value={channel.value}>{channel.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="form-label">Resultado *</Label>
                <Select 
                  value={contactData.result || undefined} 
                  onValueChange={(value) => setContactData({ ...contactData, result: value })}
                >
                  <SelectTrigger data-testid="select-contact-result">
                    <SelectValue placeholder="Seleccionar resultado" />
                  </SelectTrigger>
                  <SelectContent>
                    {contactResults.map((result) => (
                      <SelectItem key={result.value} value={result.value}>{result.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="form-label">Observaciones</Label>
                <textarea
                  value={contactData.observations}
                  onChange={(e) => setContactData({ ...contactData, observations: e.target.value })}
                  className="form-input min-h-[80px] resize-none"
                  data-testid="input-contact-observations"
                />
              </div>

              <div>
                <Label className="form-label">Próximo Seguimiento</Label>
                <Input
                  type="datetime-local"
                  value={contactData.next_followup}
                  onChange={(e) => setContactData({ ...contactData, next_followup: e.target.value })}
                  className="form-input"
                  data-testid="input-contact-next-followup"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowContactDialog(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={saving || !contactData.channel || !contactData.result}
                  data-testid="btn-save-contact"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Registrar
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
